import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Stack,
  TextField,
  Checkbox,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
  IconButton,
  Tooltip,
  Button,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AddIcon from '@mui/icons-material/Add';

const DEFAULT_COLUMNS = ['Opção 1', 'Opção 2', 'Opção 3', 'Opção 4', 'Opção 5'];

const MAX_QUESTION_LENGTH = 150;
const MAX_STATEMENT_LENGTH = 100;
const MAX_OPTION_LENGTH = 40;

const normalizeStatements = (list) =>
  (Array.isArray(list) ? list : []).map((statement, index) => ({
    name: statement && typeof statement.name === 'string' ? statement.name : '',
    order: index,
    options:
      statement && Array.isArray(statement.options) ? statement.options.slice() : [],
  }));

const initialColumns = (list) => {
  if (list.length > 0 && Array.isArray(list[0].options) && list[0].options.length > 0) {
    return list[0].options.slice();
  }
  return DEFAULT_COLUMNS.slice();
};

const LikertBuilder = ({
  statements,
  onChange,
  intl,
  disabled,
  selectionMode,
  onSelectionModeChange,
  questionValue,
  onQuestionChange,
}) => {
  const list = normalizeStatements(statements);
  const [columns, setColumns] = useState(() => initialColumns(list));

  const t = (id) => intl.formatMessage({ id });
  const isCheckbox = Number(selectionMode) === 2;

  const emit = (nextColumns, nextList) => {
    setColumns(nextColumns);
    onChange(
      normalizeStatements(nextList).map((statement, index) => ({
        name: statement.name,
        order: index,
        options: nextColumns.slice(),
      }))
    );
  };

  useEffect(() => {
    if (!Array.isArray(statements) || statements.length === 0) {
      onChange([{ name: '', order: 0, options: columns.slice() }]);
    }
  }, []);

  const updateColumn = (columnIndex, value) => {
    emit(
      columns.map((label, position) => (position === columnIndex ? value : label)),
      list
    );
  };

  const addColumn = () => {
    emit([...columns, `Opção ${columns.length + 1}`], list);
  };

  const removeColumn = (columnIndex) => {
    if (columns.length <= 1) return;
    emit(columns.filter((label, position) => position !== columnIndex), list);
  };

  const updateStatementName = (statementIndex, value) => {
    emit(
      columns,
      list.map((statement, position) =>
        position === statementIndex ? { ...statement, name: value } : statement
      )
    );
  };

  const removeStatement = (statementIndex) => {
    emit(columns, list.filter((statement, position) => position !== statementIndex));
  };

  const addStatement = () => {
    emit(columns, [...list, { name: '', order: list.length, options: columns.slice() }]);
  };

  const deleteIconSx = {
    color: 'text.disabled',
    '&:hover': { color: 'error.main' },
  };
  const headCellSx = { px: 1, py: 1.25, verticalAlign: 'bottom' };
  const bodyCellSx = { px: 1, py: 1.5, verticalAlign: 'middle' };
  const roundedInputSx = { '& .MuiOutlinedInput-root': { borderRadius: '10px' } };

  const modeOption = (value, label, hint) => (
    <FormControlLabel
      value={value}
      control={<Radio color="primary" size="small" disabled={disabled} />}
      label={
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography sx={{ fontSize: 14 }}>{label}</Typography>
          <Tooltip title={hint} arrow>
            <HelpOutlineIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
          </Tooltip>
        </Stack>
      }
    />
  );

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '14px',
        p: 2.5,
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5,
      }}
    >
      {typeof onQuestionChange === 'function' && (
        <TextField
          fullWidth
          variant="outlined"
          label={t('likert.builder.question')}
          value={questionValue || ''}
          onChange={(event) => onQuestionChange(event.target.value)}
          disabled={disabled}
          inputProps={{ maxLength: MAX_QUESTION_LENGTH }}
          sx={roundedInputSx}
        />
      )}

      <Box>
        <FormLabel sx={{ display: 'block', mb: 0.5, fontSize: 13, fontWeight: 700 }}>
          {t('likert.builder.mode')}
        </FormLabel>
        <RadioGroup
          row
          value={String(isCheckbox ? 2 : 1)}
          onChange={(event) => onSelectionModeChange(Number(event.target.value))}
        >
          {modeOption('1', t('likert.builder.mode.single'), t('likert.builder.mode.single.hint'))}
          {modeOption('2', t('likert.builder.mode.multiple'), t('likert.builder.mode.multiple.hint'))}
        </RadioGroup>
      </Box>

      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '12px',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ overflowX: 'auto' }}>
          <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%', minWidth: 480 }}>
            <Box
              component="thead"
              sx={{
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                '& th': { borderBottom: '1px solid', borderColor: 'divider' },
              }}
            >
              <Box component="tr">
                <Box component="th" sx={{ ...headCellSx, textAlign: 'left', minWidth: 240 }} />
                {columns.map((label, columnIndex) => (
                  <Box component="th" key={columnIndex} sx={{ ...headCellSx, minWidth: 150 }}>
                    <Stack direction="row" spacing={0.25} alignItems="center" justifyContent="center">
                      <TextField
                        variant="standard"
                        size="small"
                        value={label}
                        onChange={(event) => updateColumn(columnIndex, event.target.value)}
                        disabled={disabled}
                        inputProps={{
                          maxLength: MAX_OPTION_LENGTH,
                          style: { textAlign: 'center', fontWeight: 600, fontSize: 13 },
                        }}
                      />
                      <Tooltip title={t('likert.builder.remove')} arrow>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => removeColumn(columnIndex)}
                            disabled={disabled || columns.length <= 1}
                            aria-label={t('likert.builder.remove')}
                            sx={deleteIconSx}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Box>
                ))}
                <Box component="th" sx={{ ...headCellSx, width: 52, textAlign: 'center' }}>
                  <Tooltip title={t('likert.builder.add.point')} arrow>
                    <span>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={addColumn}
                        disabled={disabled}
                        aria-label={t('likert.builder.add.point')}
                        sx={{
                          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                          '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16) },
                        }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            </Box>
            <Box component="tbody">
              {list.map((statement, statementIndex) => (
                <Box
                  component="tr"
                  key={statementIndex}
                  sx={{
                    '&:not(:first-of-type) > td': { borderTop: '1px solid', borderColor: 'divider' },
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Box component="td" sx={{ ...bodyCellSx, minWidth: 240 }}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Tooltip title={t('likert.builder.remove')} arrow>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => removeStatement(statementIndex)}
                            disabled={disabled || list.length <= 1}
                            aria-label={t('likert.builder.remove')}
                            sx={deleteIconSx}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <TextField
                        fullWidth
                        size="small"
                        variant="standard"
                        placeholder={t('likert.builder.statement.name')}
                        value={statement.name}
                        onChange={(event) => updateStatementName(statementIndex, event.target.value)}
                        disabled={disabled}
                        inputProps={{ maxLength: MAX_STATEMENT_LENGTH, style: { fontSize: 14 } }}
                      />
                    </Stack>
                  </Box>
                  {columns.map((label, columnIndex) => (
                    <Box component="td" key={columnIndex} sx={{ ...bodyCellSx, textAlign: 'center' }}>
                      {isCheckbox ? (
                        <Checkbox size="small" color="primary" disabled />
                      ) : (
                        <Radio size="small" color="primary" disabled />
                      )}
                    </Box>
                  ))}
                  <Box component="td" sx={{ ...bodyCellSx }} />
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      <Button
        color="primary"
        startIcon={<AddIcon />}
        onClick={addStatement}
        disabled={disabled}
        sx={{ textTransform: 'none', borderRadius: '10px', alignSelf: 'flex-start' }}
      >
        {t('likert.builder.add.statement')}
      </Button>
    </Box>
  );
};

LikertBuilder.propTypes = {
  statements: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      order: PropTypes.number,
      options: PropTypes.arrayOf(PropTypes.string),
    })
  ),
  onChange: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  selectionMode: PropTypes.number,
  onSelectionModeChange: PropTypes.func,
  questionValue: PropTypes.string,
  onQuestionChange: PropTypes.func,
};

LikertBuilder.defaultProps = {
  statements: [],
  disabled: false,
  selectionMode: 1,
  onSelectionModeChange: () => {},
  questionValue: '',
  onQuestionChange: null,
};

export default LikertBuilder;
